import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fireEvent } from "@/lib/ads";
import { useAuth } from "@/lib/auth";
import { Sparkles, Send, CalendarCheck, Loader2, Check } from "lucide-react";

const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// Concrete upcoming slots derived from the owner's weekly visiting windows.
function genSlots(windows) {
  if (!Array.isArray(windows) || !windows.length) return [];
  const out = [];
  const now = new Date();
  for (let d = 0; d < 14 && out.length < 8; d++) {
    const date = new Date(now);
    date.setDate(now.getDate() + d);
    for (const w of windows) {
      if (DOW[w.day] !== date.getDay()) continue;
      const fh = parseInt(String(w.from || "10:00").split(":")[0], 10) || 10;
      const th = parseInt(String(w.to || "12:00").split(":")[0], 10) || 12;
      for (let h = fh; h <= th && out.length < 8; h++) {
        if (d === 0 && h <= now.getHours()) continue;
        const label = `${date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}, ${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"}`;
        out.push(label);
      }
    }
  }
  return out;
}

const SUGGESTED = ["Is it furnished?", "What's the deposit?", "Is it available now?"];

export default function ListingConcierge({ property, isOwner }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [booking, setBooking] = useState(false);
  const [slot, setSlot] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [booked, setBooked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing, booking]);
  useEffect(() => {
    if (messages.length === 0)
      setMessages([{ role: "bot", text: `Hi! I'm the assistant for this ${property.bhk} BHK in ${property.locality}. Ask me anything, or book a visit — the owner will be notified.` }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isOwner) return null;

  const ask = async (text) => {
    const msg = (text || "").trim();
    if (!msg) return;
    const history = messages.filter((m) => m.text).map((m) => ({ role: m.role === "user" ? "user" : "bot", text: m.text }));
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setTyping(true);
    try {
      const { data } = await api.post(`/properties/${property.id}/concierge`, { message: msg, history });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: e?.response?.data?.detail || "I couldn't answer that — try booking a visit to ask the owner directly." }]);
    } finally { setTyping(false); }
  };

  const slots = genSlots(property.visiting_windows);

  const submitVisit = async () => {
    if (!name.trim() || !phone.trim()) { toast.error("Add your name and phone"); return; }
    if (!slot.trim()) { toast.error("Pick or type a preferred time"); return; }
    setSubmitting(true);
    try {
      await api.post(`/properties/${property.id}/visit-requests`, {
        tenant_name: name.trim(), tenant_phone: phone.trim(),
        tenant_email: email.trim() || null, slot: slot.trim(),
      });
      setBooked(true);
      fireEvent("visit_requested", { property_id: property.id });
      toast.success("Visit request sent to the owner");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the request");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-2xl border border-brand-subtle bg-white overflow-hidden" data-testid="listing-concierge">
      <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-br from-[#7A1E2A] to-[#5C1420]">
        <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
          <Sparkles size={16} className="text-[#F3E4BC]" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm leading-tight">Ask about this home</p>
          <p className="text-white/70 text-[11px]">Instant answers · book a visit — owner gets notified</p>
        </div>
      </div>

      <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-3" data-testid="concierge-messages">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[85%] text-sm px-3.5 py-2 ${m.role === "user"
              ? "bg-[#7A1E2A] text-white rounded-2xl rounded-br-sm"
              : "bg-[#FBF6EC] text-gray-800 rounded-2xl rounded-bl-sm border border-brand-subtle"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {typing && <div className="flex justify-start"><div className="bg-[#FBF6EC] border border-brand-subtle rounded-2xl rounded-bl-sm px-3.5 py-2 flex gap-1">
          {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#C9A24A] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
        </div></div>}
        <div ref={endRef} />
      </div>

      <div className="px-4 pb-4">
        {/* Suggested questions before any interaction */}
        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTED.map((q) => (
              <button key={q} onClick={() => ask(q)} data-testid="concierge-suggestion"
                className="text-xs px-3 py-1.5 rounded-full border border-brand-subtle text-gray-600 hover:border-[#C9A24A] hover:text-[#7A1E2A] transition-colors">{q}</button>
            ))}
          </div>
        )}

        {/* Book a visit */}
        {!booking && !booked && (
          <button onClick={() => setBooking(true)} data-testid="book-visit-btn"
            className="w-full mb-3 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-[#C9A24A] hover:bg-[#b48f3f] transition-colors">
            <CalendarCheck size={16} /> Book a visit
          </button>
        )}

        {booked && (
          <div className="mb-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="visit-booked">
            <Check size={16} /> Visit request sent — the owner has been notified and will confirm shortly.
          </div>
        )}

        {booking && !booked && (
          <div className="mb-3 rounded-xl border border-brand-subtle p-3 space-y-2.5" data-testid="visit-form">
            <p className="text-xs font-semibold brand-forest">Pick a time</p>
            {slots.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button key={s} onClick={() => setSlot(s)} data-testid="visit-slot-chip"
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${slot === s ? "bg-[#7A1E2A] text-white border-[#7A1E2A]" : "border-brand-subtle text-gray-600 hover:border-[#C9A24A]"}`}>{s}</button>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-gray-500">The owner hasn't set visiting hours — suggest a time below.</p>
            )}
            <input value={slot} onChange={(e) => setSlot(e.target.value)} placeholder="Preferred time (e.g. Sat 11 AM)"
              data-testid="visit-slot-input" className="w-full rounded-lg px-3 py-2 text-sm border border-brand-subtle outline-none focus:border-[#C9A24A]" />
            <div className="grid grid-cols-2 gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="visit-name"
                className="rounded-lg px-3 py-2 text-sm border border-brand-subtle outline-none focus:border-[#C9A24A]" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (+91…)" data-testid="visit-phone"
                className="rounded-lg px-3 py-2 text-sm border border-brand-subtle outline-none focus:border-[#C9A24A]" />
            </div>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" data-testid="visit-email"
              className="w-full rounded-lg px-3 py-2 text-sm border border-brand-subtle outline-none focus:border-[#C9A24A]" />
            <button onClick={submitVisit} disabled={submitting} data-testid="visit-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white bg-[#7A1E2A] hover:bg-[#661620] disabled:opacity-60">
              {submitting ? <Loader2 size={15} className="animate-spin" /> : "Request visit"}
            </button>
          </div>
        )}

        {/* Ask input */}
        <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5 border border-brand-subtle">
          <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="concierge-input"
            placeholder="Ask a question…" className="flex-1 bg-transparent outline-none text-sm min-w-0" />
          <button type="submit" data-testid="concierge-send" className="shrink-0 w-9 h-9 rounded-full bg-[#7A1E2A] text-white flex items-center justify-center hover:bg-[#661620]"><Send size={15} /></button>
        </form>
      </div>
    </div>
  );
}
